// src/pages/AdminProductForm.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/axios";
import { useI18n } from "../../i18n/I18nContext";
import AdminProductVariants from "./ProductVariants";

const MAX_IMAGES = 4;

const emptyForm = {
  id: null,
  name: "",
  slug: "",
  description: "",
  features: "",
  sku: "",
  is_active: true,
  images: Array(MAX_IMAGES).fill(null),
  category_ids: [],
};

function StatusBadge({ active }) {
  const { t } = useI18n();

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
        active
          ? "border-green-200 bg-green-50 text-green-700"
          : "border-gray-200 bg-gray-50 text-gray-700"
      }`}
    >
      {active ? t("Ativo") : t("Inativo")}
    </span>
  );
}

function normalizePaginated(resData) {
  return resData?.data?.data || resData?.data || [];
}

function createImageItem(file, slotIndex, current = null) {
  return {
    id:
      current?.id ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `new-${Date.now()}-${slotIndex}`),
    existingId: current?.existingId ?? null,
    file,
    previewUrl: URL.createObjectURL(file),
    alt_text: current?.alt_text || "",
    is_primary: current?.is_primary || false,
    position: slotIndex,
    isExisting: false,
  };
}

function normalizeSlots(images) {
  const slots = Array(MAX_IMAGES).fill(null);

  (images || []).forEach((img, index) => {
    if (!img) return;

    const pos =
      typeof img.position === "number" && img.position >= 0 && img.position < MAX_IMAGES
        ? img.position
        : index;

    slots[pos] = {
      ...img,
      position: pos,
    };
  });

  const firstFilledIndex = slots.findIndex(Boolean);

  if (firstFilledIndex >= 0 && !slots.some((img) => img?.is_primary)) {
    slots[firstFilledIndex] = {
      ...slots[firstFilledIndex],
      is_primary: true,
    };
  }

  return slots.map((img, index) =>
    img
      ? {
          ...img,
          position: index,
        }
      : null
  );
}

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { t } = useI18n();

  const [form, setForm] = useState(emptyForm);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await api.get("/api/v1/categories");
        setAllCategories(normalizePaginated(res));
      } catch (err) {
        setErrorMsg(err?.response?.data?.message || t("Erro a carregar categorias."));
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    if (!isEdit) return;

    const loadProduct = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const res = await api.get(`/api/admin/products/${id}`);
        const product = res.data?.data ?? res.data;

        const existingImages = (product.images ?? [])
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .slice(0, MAX_IMAGES)
          .map((img, index) => ({
            id: `existing-${img.id}`,
            existingId: img.id,
            file: null,
            previewUrl: img.image_url || img.url || "",
            alt_text: img.alt_text || "",
            is_primary: (img.position ?? index) === 0,
            position: img.position ?? index,
            isExisting: true,
          }));

        setForm({
          id: product.id,
          name: product.name ?? "",
          slug: product.slug ?? "",
          description: product.description ?? "",
          features:
            typeof product.features === "string"
              ? product.features
              : product.features
              ? JSON.stringify(product.features, null, 2)
              : "",
          sku: product.sku ?? "",
          is_active: !!product.is_active,
          images: normalizeSlots(existingImages),
          category_ids: product.categories?.map((c) => c.id) ?? [],
        });
      } catch (err) {
        setErrorMsg(err?.response?.data?.message || t("Erro ao carregar produto."));
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, isEdit]);

  useEffect(() => {
    return () => {
      form.images.forEach((img) => {
        if (img && !img.isExisting && img.previewUrl) {
          URL.revokeObjectURL(img.previewUrl);
        }
      });
    };
  }, [form.images]);

  const availableCategories = useMemo(() => {
    return allCategories.filter((c) => !form.category_ids.includes(c.id));
  }, [allCategories, form.category_ids]);

  const selectedCategories = useMemo(() => {
    return allCategories.filter((c) => form.category_ids.includes(c.id));
  }, [allCategories, form.category_ids]);

  const onPickImages = (files) => {
    if (!files?.length) return;

    setErrorMsg("");

    setForm((prev) => {
      const next = [...prev.images];
      const emptySlots = next
        .map((img, index) => (img ? null : index))
        .filter((index) => index !== null);

      if (emptySlots.length === 0) {
        setErrorMsg(`${t("Só podes adicionar até")} ${MAX_IMAGES} ${t("imagens por produto.")}`);
        return prev;
      }

      const picked = Array.from(files).slice(0, emptySlots.length);

      if (files.length > emptySlots.length) {
        setErrorMsg(`${t("Só podes adicionar até")} ${MAX_IMAGES} ${t("imagens por produto.")}`);
      }

      picked.forEach((file, i) => {
        const slotIndex = emptySlots[i];
        next[slotIndex] = createImageItem(file, slotIndex, next[slotIndex]);
      });

      return {
        ...prev,
        images: normalizeSlots(next),
      };
    });
  };

  const replaceImageAt = (slotIndex, file) => {
    if (!file) return;

    setErrorMsg("");

    setForm((prev) => {
      const next = [...prev.images];
      const current = next[slotIndex];

      if (current && !current.isExisting && current.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      next[slotIndex] = createImageItem(file, slotIndex, current);

      return {
        ...prev,
        images: normalizeSlots(next),
      };
    });
  };

  const setPrimaryImage = (imageId) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.map((img) =>
        img
          ? {
              ...img,
              is_primary: img.id === imageId,
            }
          : null
      ),
    }));
  };

  const updateImageField = (imageId, field, value) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.map((img) =>
        img && img.id === imageId ? { ...img, [field]: value } : img
      ),
    }));
  };

  const removeImage = (imageId) => {
    setForm((prev) => {
      const indexToRemove = prev.images.findIndex((img) => img?.id === imageId);

      if (indexToRemove === -1) return prev;

      const imageToRemove = prev.images[indexToRemove];

      if (imageToRemove && !imageToRemove.isExisting && imageToRemove.previewUrl) {
        URL.revokeObjectURL(imageToRemove.previewUrl);
      }

      const next = [...prev.images];
      next[indexToRemove] = null;

      return {
        ...prev,
        images: normalizeSlots(next),
      };
    });
  };

  const hasImageInDirection = (index, direction) => {
    let i = index + direction;

    while (i >= 0 && i < MAX_IMAGES) {
      if (form.images[i]) return true;
      i += direction;
    }

    return false;
  };

  const moveImage = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.images];

      if (!next[index]) return prev;

      let targetIndex = index + direction;

      while (
        targetIndex >= 0 &&
        targetIndex < MAX_IMAGES &&
        !next[targetIndex]
      ) {
        targetIndex += direction;
      }

      if (targetIndex < 0 || targetIndex >= MAX_IMAGES) return prev;

      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];

      return {
        ...prev,
        images: next.map((img, idx) =>
          img
            ? {
                ...img,
                position: idx,
              }
            : null
        ),
      };
    });
  };

  const addCategory = (catId) => {
    setForm((prev) => {
      if (prev.category_ids.includes(catId)) return prev;
      return {
        ...prev,
        category_ids: [...prev.category_ids, catId],
      };
    });
  };

  const removeCategory = (catId) => {
    setForm((prev) => ({
      ...prev,
      category_ids: prev.category_ids.filter((id) => id !== catId),
    }));
  };

  const getFirstLaravelError = (err) => {
    const data = err?.response?.data;

    if (data?.errors) {
      const firstKey = Object.keys(data.errors)[0];
      return data.errors[firstKey]?.[0] || t("Erro de validação.");
    }

    return data?.message || t("Erro inesperado.");
  };

  const buildBasePayload = () => ({
    name: form.name,
    slug: form.slug || undefined,
    description: form.description || null,
    features: form.features || null,
    sku: form.sku || null,
    is_active: form.is_active ? 1 : 0,
    category_ids: form.category_ids,
  });

  const buildMultipart = (base, includeMethodPut = false) => {
    const fd = new FormData();

    if (includeMethodPut) {
      fd.append("_method", "PUT");
    }

    Object.entries(base).forEach(([k, v]) => {
      if (v === undefined || v === null) return;

      if (Array.isArray(v)) {
        if (k === "category_ids") {
          v.forEach((val) => fd.append("category_ids[]", String(val)));
        }
        return;
      }

      fd.append(k, String(v));
    });

    const orderedSlots = form.images.slice(0, MAX_IMAGES);

    orderedSlots.forEach((img, slotIndex) => {
      if (img?.isExisting && img.existingId) {
        fd.append("existing_images[]", String(img.existingId));
        fd.append("existing_alt_texts[]", img.alt_text || "");
        fd.append("existing_positions[]", String(slotIndex));
        fd.append("existing_is_primary[]", img.is_primary ? "1" : "0");
      } else if (img?.file instanceof File) {
        fd.append(`new_images[${slotIndex}]`, img.file, img.file.name);
        fd.append(`new_alt_texts[${slotIndex}]`, img.alt_text || "");
        fd.append(`new_positions[${slotIndex}]`, String(slotIndex));
        fd.append(`new_is_primary[${slotIndex}]`, img.is_primary ? "1" : "0");
      }
    });

    return fd;
  };

  const saveProduct = async () => {
    setSaving(true);
    setErrorMsg("");

    try {
      const base = buildBasePayload();
      const hasAnyImages = form.images.some(Boolean);

      if (isEdit) {
        if (hasAnyImages) {
          const fd = buildMultipart(base, true);
          await api.post(`/api/admin/products/${form.id}`, fd);
        } else {
          await api.put(`/api/admin/products/${form.id}`, base);
        }
      } else {
        let res;

        if (hasAnyImages) {
          const createFd = new FormData();

          Object.entries(base).forEach(([k, v]) => {
            if (v === undefined || v === null) return;

            if (Array.isArray(v)) {
              if (k === "category_ids") {
                v.forEach((val) => createFd.append("category_ids[]", String(val)));
              }
              return;
            }

            createFd.append(k, String(v));
          });

          form.images.forEach((img, slotIndex) => {
            if (img?.file instanceof File) {
              createFd.append(`images[${slotIndex}]`, img.file, img.file.name);
              createFd.append(`alt_texts[${slotIndex}]`, img.alt_text || "");
              createFd.append(`positions[${slotIndex}]`, String(slotIndex));
            }
          });

          const primaryIndex = form.images.findIndex((img) => img?.is_primary);
          createFd.append("primary_index", primaryIndex >= 0 ? String(primaryIndex) : "0");

          res = await api.post("/api/admin/products", createFd);
        } else {
          res = await api.post("/api/admin/products", base);
        }

        const createdProduct = res.data?.data ?? res.data;
        navigate(`/admin/products/${createdProduct.id}/edit`);
        return;
      }

      navigate("/admin/products");
    } catch (err) {
      setErrorMsg(getFirstLaravelError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">{t("A carregar...")}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 px-6 py-10">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? `${t("Editar Produto")} #${form.id}` : t("Criar Produto")}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isEdit
                ? t("Edita os dados do produto, imagens e gere as variantes.")
                : t("Cria o produto e adiciona até 4 imagens.")}
            </p>
          </div>
        </div>

        {errorMsg && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{t("Nome")} *</label>
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("Slug (opcional)")}
              </label>
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("SKU (opcional)")}
              </label>
              <input
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.sku}
                onChange={(e) => setForm((p) => ({ ...p, sku: e.target.value }))}
              />
            </div>

            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      is_active: e.target.checked,
                    }))
                  }
                />
                {t("Ativo")}
              </label>

              <StatusBadge active={form.is_active} />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t("Descrição")}</label>
            <textarea
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  description: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Features</label>
            <textarea
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={form.features}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  features: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-900">{t("Categorias")}</h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-gray-200">
                <div className="border-b bg-gray-50 px-3 py-2 text-xs font-semibold uppercase text-gray-600">
                  {t("Disponíveis")}
                </div>

                <div className="max-h-60 overflow-y-auto">
                  {availableCategories.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-gray-500">
                      {t("Sem categorias disponíveis.")}
                    </div>
                  ) : (
                    availableCategories.map((cat) => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        <span>{cat.name}</span>
                        <button
                          type="button"
                          onClick={() => addCategory(cat.id)}
                          className="rounded bg-green-600 px-2 py-1 text-xs font-semibold text-white hover:bg-green-500"
                        >
                          +
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-gray-200">
                <div className="border-b bg-gray-50 px-3 py-2 text-xs font-semibold uppercase text-gray-600">
                  {t("Associadas")}
                </div>

                <div className="max-h-60 overflow-y-auto">
                  {selectedCategories.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-gray-500">
                      {t("Nenhuma categoria associada.")}
                    </div>
                  ) : (
                    selectedCategories.map((cat) => (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        <span>{cat.name}</span>
                        <button
                          type="button"
                          onClick={() => removeCategory(cat.id)}
                          className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-500"
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{t("Imagens do produto")}</h3>
                <p className="mt-1 text-xs text-gray-500">
                  {t("Máximo de")} {MAX_IMAGES} {t("imagens. Escolhe a principal e ordena as restantes.")}
                </p>
              </div>

              {form.images.some((img) => !img) && (
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                  </svg>
                  {t("Adicionar imagens")}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      onPickImages(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {Array.from({ length: MAX_IMAGES }).map((_, slotIndex) => {
                const img = form.images[slotIndex];

                if (!img) {
                  return (
                    <label
                      key={slotIndex}
                      className="group flex aspect-square cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center hover:border-indigo-400 hover:bg-indigo-50/40 transition"
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm border border-gray-200 text-gray-500 group-hover:text-indigo-600 transition">
                        <svg
                          className="h-7 w-7"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                        </svg>
                      </div>
                      <p className="mt-4 text-sm font-semibold text-gray-700">
                        {t("Adicionar imagem")}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Slot {slotIndex + 1} {t("de")} {MAX_IMAGES}
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          replaceImageAt(slotIndex, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  );
                }

                return (
                  <div
                    key={img.id}
                    className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                  >
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      <img
                        src={img.previewUrl}
                        alt={img.alt_text || `${t("Imagem")} ${slotIndex + 1}`}
                        className="h-full w-full object-cover"
                      />

                      <div className="absolute left-3 top-3 flex gap-2">
                        <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white">
                          #{slotIndex + 1}
                        </span>
                        {img.is_primary && (
                          <span className="rounded-full bg-green-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                            {t("Principal")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3 p-3">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700">
                          Alt text
                        </label>
                        <input
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                          placeholder={t("Descrição da imagem")}
                          value={img.alt_text}
                          onChange={(e) =>
                            updateImageField(img.id, "alt_text", e.target.value)
                          }
                        />
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setPrimaryImage(img.id)}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                            img.is_primary
                              ? "bg-green-600 text-white"
                              : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {img.is_primary ? t("Imagem principal") : t("Definir principal")}
                        </button>

                        <button
                          type="button"
                          disabled={!hasImageInDirection(slotIndex, -1)}
                          onClick={() => moveImage(slotIndex, -1)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                        >
                          ←
                        </button>

                        <button
                          type="button"
                          disabled={!hasImageInDirection(slotIndex, 1)}
                          onClick={() => moveImage(slotIndex, 1)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                        >
                          →
                        </button>

                        <label className="cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100">
                          {t("Trocar")}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              replaceImageAt(slotIndex, e.target.files?.[0]);
                              e.target.value = "";
                            }}
                          />
                        </label>

                        <button
                          type="button"
                          onClick={() => removeImage(img.id)}
                          className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500"
                        >
                          {t("Remover")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => navigate("/admin/products")}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            {t("Voltar")}
          </button>

          <button
            onClick={saveProduct}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
            disabled={saving}
          >
            {saving
              ? t("A guardar…")
              : isEdit
              ? t("Guardar produto")
              : t("Criar produto")}
          </button>
        </div>

        {isEdit && <AdminProductVariants productId={form.id} productName={form.name} />}
      </div>
    </div>
  );
}